import { datadogRum } from '@datadog/browser-rum'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useEffect, useRef, useState } from 'react'
import * as paymentService from '../core/paymentService'
import { CKO_REDIRECT_APM_OPTION_ID_KEY } from './use-cko-redirect-apm-execution'

interface UseFpCkoRedirectApmExecutionArgs {
  ckoPaymentId: string | null | undefined
  paymentTransactionId: number | string | null | undefined
  paymentSuccessUrl: string
  paymentFallbackUrl: string
  onReconciled?: () => void
}

export const useFpCkoRedirectApmExecution = ({
  ckoPaymentId,
  paymentTransactionId,
  paymentSuccessUrl,
  paymentFallbackUrl,
  onReconciled,
}: UseFpCkoRedirectApmExecutionArgs) => {
  const { trigger: executePayment } = useApiFpPaymentExecution()
  const [isCkoExecuting, setIsCkoExecuting] = useState(
    () => !!paymentTransactionId && !!sessionStorage.getItem(CKO_REDIRECT_APM_OPTION_ID_KEY),
  )
  const hasExecuted = useRef(false)

  useEffect(function executeFpCkoRedirectApmPayment() {
    if (!paymentTransactionId || hasExecuted.current) return
    const storedOptionId = sessionStorage.getItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
    if (!storedOptionId) return
    hasExecuted.current = true

    paymentService.executeWithCkoReconciliationRetry(() => executePayment({
      payment_transaction_id: Number(paymentTransactionId),
      service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
      user_payment_token_info: null,
      payment_success_url: paymentSuccessUrl,
      payment_fallback_url: paymentFallbackUrl,
      payment_option_id: Number(storedOptionId),
      cko_payment_id: ckoPaymentId ?? undefined,
    })).then((executionResponse) => {
      sessionStorage.removeItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
      const redirectUrl = executionResponse?.data?.redirect_url
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
      else {
        setIsCkoExecuting(false)
        onReconciled?.()
      }
    }).catch((executionError: unknown) => {
      datadogRum.addError(new Error('[useFpCkoRedirectApmExecution] /payment/execution failed'), {
        function: 'useFpCkoRedirectApmExecution.executePayment',
        paymentTransactionId,
        ckoPaymentId,
        error: executionError,
      })
      sessionStorage.removeItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
      setIsCkoExecuting(false)
    })
  }, [ckoPaymentId, paymentTransactionId, paymentSuccessUrl, paymentFallbackUrl, executePayment, onReconciled])

  return { isCkoExecuting }
}
